/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { RefObject } from 'preact';
import { LegendItem, LegendSection } from './Legend.type';
import { useMemo, useRef, useLayoutEffect, useState } from 'preact/hooks';
import { Font, useTextDimensions } from '../hooks/PRIVATE_useTextDimensions';

type Props<K> = {
  sections?: LegendSection<K>[];
  items?: LegendItem<K>[];
  resolvedVars: Record<string, any>;
  rootRef: RefObject<HTMLDivElement>;
  rootDimsRef: RefObject<DOMRect | undefined>;
  orientation?: 'vertical' | 'horizontal';
  legendFontStylesObj?: Font;
  symbolWidth?: number;
};

type getTextDimensionsType = NonNullable<
  NonNullable<ReturnType<typeof useTextDimensions>>['getTextDimensions']
>;

export type LayoutInfo = {
  isWrapped: boolean;
  maxItemWidth: number;
  itemsPerRow: number;
};

/**
 * Returns maximum of the width among the items in a section of legend also return the width of the entire section.
 * @param items Legend items array
 * @param unitLength Base  element
 * @param getTextDimensions Text width measurement function
 * @param legendFontStylesObj Legend text font style properties object
 * @param symbolWidth Width of legend symbol
 *
 * @returns
 */
const getMaxWidth = <K>(
  items: LegendItem<K>[],
  unitLength: number,
  getTextDimensions: getTextDimensionsType,
  legendFontStylesObj?: Font,
  symbolWidth?: number
) => {
  let maxItemWidth = 0;
  let horizontalSectionWidth = 0;
  let itemSymbolWidth = 0;
  if (symbolWidth) {
    itemSymbolWidth = unitLength * 1.5 + symbolWidth;
  } else {
    //itemSymbolWidth = marginLeft + marginRight + width below expression is simplified
    itemSymbolWidth = 4 * unitLength;
  }
  items?.forEach((item) => {
    const legendItem = item as LegendItem<K>;
    const { width } = getTextDimensions(legendItem.text, legendFontStylesObj);
    //itemTextWidth = paddingLeft + paddingRight + width
    const itemTextWidth = unitLength * 2.5 + width;
    //horizontalSectionWidth = total width of the section
    horizontalSectionWidth += itemSymbolWidth + itemTextWidth;
    maxItemWidth = Math.max(maxItemWidth, Math.ceil(itemSymbolWidth + itemTextWidth));
  });

  return { maxItemWidth, horizontalSectionWidth };
};

export function useLegendLayout<K>({
  resolvedVars,
  sections,
  items,
  rootRef,
  rootDimsRef,
  orientation,
  legendFontStylesObj = {},
  symbolWidth
}: Props<K>) {
  const { textMeasureContent, getTextDimensions } = useTextDimensions();
  const orientationRef = useRef(orientation);
  const hasOrientationChanged = orientationRef.current !== orientation;
  const [needsRerender, setRerender] = useState(false);

  useLayoutEffect(() => {
    if (hasOrientationChanged) {
      //rerender the Legend
      orientationRef.current = orientation;
      setRerender(!needsRerender);
    }
  }, [hasOrientationChanged, needsRerender, orientation]);

  const layoutInfo = useMemo(() => {
    if (
      !rootRef.current ||
      !Object.keys(resolvedVars).length ||
      !getTextDimensions ||
      hasOrientationChanged ||
      orientation === 'vertical'
    ) {
      return;
    }
    const unitLength = parseFloat(resolvedVars['unit']);
    // recalculate rootDims everytime items change.
    rootDimsRef.current = rootRef?.current?.getBoundingClientRect();
    let maxWidth = 0;
    let isWrapped = false;
    if (sections) {
      sections.forEach((section) => {
        const { maxItemWidth, horizontalSectionWidth } = getMaxWidth(
          section.items,
          unitLength,
          getTextDimensions,
          legendFontStylesObj,
          symbolWidth
        );

        if (!isWrapped) {
          isWrapped = horizontalSectionWidth > rootDimsRef.current?.width!;
        }
        maxWidth = Math.max(maxWidth, maxItemWidth);
      });
    }
    if (items) {
      const { maxItemWidth, horizontalSectionWidth } = getMaxWidth(
        items,
        unitLength,
        getTextDimensions,
        legendFontStylesObj,
        symbolWidth
      );
      maxWidth = maxItemWidth;
      isWrapped = horizontalSectionWidth > rootDimsRef.current?.width!;
    }
    const legendLayoutInfo = {
      maxItemWidth: Math.min(maxWidth, rootDimsRef.current?.width!),
      itemsPerRow: Math.max(1, Math.floor(rootDimsRef.current?.width! / maxWidth)),
      isWrapped
    };
    return legendLayoutInfo;
  }, [
    sections,
    items,
    rootDimsRef,
    rootRef,
    legendFontStylesObj,
    resolvedVars,
    symbolWidth,
    getTextDimensions,
    orientation,
    hasOrientationChanged
  ]);

  return {
    textMeasureContent,
    layoutInfo
  };
}
